import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import {
  Button,
  Chip,
  DialogContent,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AlertsBarContainer,
  AlertsBarVariant,
  DistaDialog,
  DistaDialogHeader,
  DistaMarkdownViewer,
} from "@sista/dista-ui";
import { IdmClientContext } from "@sista/idm-client";
import {
  Organization,
  SistaId,
  SmartButton,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import {
  OrganizationChangeNoticeDTO,
  OrganizationChangeNoticeStatus,
} from "@sista/library-stasi/dist/api/utils/buildRejstrikClient";
import { SistaFistoSingle, SistaFistoView } from "@sista/service-fisto-ui";
import { SkeletonProjectData } from "@sista/service-project-skeleton";
import { SistaSpravaUkoluList } from "@sista/service-workflow-ui";
import { SistaChip } from "@sista/sista-chip";
import { useCallback, useContext, useRef, useState } from "react";
import { FormContainer, useForm } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";
import {
  NoticeFormData,
  OrganizationChangeNoticeFormFields,
} from "../../dialogs/organization/OrganizationChangeNoticeFormFields";

type StatusConfig = {
  label: string;
  defaultValue: string;
  backgroundColor: string;
};

export const CHANGE_NOTICE_STATUS_CONFIG: Record<
  OrganizationChangeNoticeStatus,
  StatusConfig
> = {
  IN_PREPARATION: {
    label: "ucet.organizationChangeNotice.status.inPreparation",
    defaultValue: "V přípravě",
    backgroundColor: "#d9d2e9",
  },
  UNDER_REVIEW: {
    label: "ucet.organizationChangeNotice.status.underReview",
    defaultValue: "Ve schvalování",
    backgroundColor: "#aacff3",
  },
  RETURNED_FOR_COMPLETION: {
    label: "ucet.organizationChangeNotice.status.returnedForCompletion",
    defaultValue: "Vráceno k doplnění",
    backgroundColor: "#f9da7b",
  },
  IMPACT_ASSESSMENT: {
    label: "ucet.organizationChangeNotice.status.impactAssessment",
    defaultValue: "Posouzení dopadu",
    backgroundColor: "#aacff3",
  },
  ACKNOWLEDGED: {
    label: "ucet.organizationChangeNotice.status.acknowledged",
    defaultValue: "Vzato na vědomí",
    backgroundColor: "#c9e798",
  },
  CANCELLED: {
    label: "ucet.organizationChangeNotice.status.cancelled",
    defaultValue: "Zrušeno",
    backgroundColor: "#dadde1",
  },
};

export type OrganizationChangeNoticeDetailContentProps = {
  organization: Organization;
  notice: OrganizationChangeNoticeDTO;
  onBack: (() => void) | null;
  canManageAttachments?: boolean;
  onNoticeUpdated?: () => void;
  onWorkflowAction?: () => void;
  hideTasks?: boolean;
  hideAffectedProjects?: boolean;
};

export const OrganizationChangeNoticeDetailContent = (
  props: OrganizationChangeNoticeDetailContentProps
) => {
  const { t } = useTranslation("ucet");
  const { loggedAccount } = useContext(IdmClientContext);
  const rej = useRejstrikClient();
  const canEdit = props.canManageAttachments ?? false;

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<
    SkeletonProjectData[]
  >([]);
  const [projectsError, setProjectsError] = useState(false);
  const hasInteracted = useRef(false);

  const initialProjectIds = props.notice.affectedProjects
    .flatMap((ap) => ap.projects)
    .map((p) => p.id)
    .filter((id): id is string => !!id);

  const form = useForm<NoticeFormData>({
    defaultValues: {
      subject: props.notice.subject,
      description: props.notice.description ?? "",
      justification: props.notice.justification ?? "",
      projectsAffected: initialProjectIds.length > 0,
      impacts: props.notice.impacts ?? "",
    },
  });

  const organizationUrn = new SistaId(
    "organizations",
    props.organization.id
  ).asUrn();
  const changeNoticeUrn = new SistaId(
    "organizationChangeNotice",
    props.notice.id
  ).asUrn();
  const fistoContext = [organizationUrn, changeNoticeUrn];

  const taskContext = [
    organizationUrn,
    new SistaId("workflows", "organization_change_notice").asUrn(),
    changeNoticeUrn,
  ];

  const status = CHANGE_NOTICE_STATUS_CONFIG[props.notice.status] ?? {
    label: props.notice.status,
    defaultValue: props.notice.status,
    backgroundColor: "#dadde1",
  };

  const updateNotice = useCallback(
    (data: NoticeFormData) => {
      const projects = data.projectsAffected
        ? (hasInteracted.current
            ? selectedProjects
            : (props.notice.affectedProjects.flatMap(
                (ap) => ap.projects
              ) as SkeletonProjectData[])
          ).map((p) => ({
            id: p.id,
            roCfpId: p.roCfpId,
            roNotFundedId: p.roNotFundedId ?? undefined,
            roContractConclusionId: p.roContractConclusionId ?? undefined,
            roImplementationId: p.roImplementationId ?? undefined,
            roEndedId: p.roEndedId ?? undefined,
            providerId: p.providerId ?? undefined,
          }))
        : [];
      return rej.updateOrganizationChangeNoticeDetail({
        organizationId: props.organization.id,
        changeNoticeId: props.notice.id,
        subject: data.subject,
        description: data.description,
        justification: data.justification,
        impacts: data.projectsAffected ? data.impacts : undefined,
        projects,
        fileIds: [],
      });
    },
    [
      rej,
      props.organization.id,
      props.notice.id,
      props.notice.affectedProjects,
      selectedProjects,
    ]
  );
  const [updateNoticeCall, updateNoticeState] = useSafeSistaAsync(updateNotice);

  const handleEditSubmit = useCallback(
    async (data: NoticeFormData) => {
      if (
        data.projectsAffected &&
        !hasInteracted.current &&
        initialProjectIds.length === 0
      ) {
        setProjectsError(true);
        return;
      }
      if (
        data.projectsAffected &&
        hasInteracted.current &&
        selectedProjects.length === 0
      ) {
        setProjectsError(true);
        return;
      }
      const result = await updateNoticeCall(data);
      if (result == null) return;
      setShowEditDialog(false);
      props.onNoticeUpdated?.();
    },
    [updateNoticeCall, props, selectedProjects, initialProjectIds]
  );

  const handleOpenEdit = useCallback(() => {
    hasInteracted.current = false;
    setSelectedProjects([]);
    setProjectsError(false);
    form.reset({
      subject: props.notice.subject,
      description: props.notice.description ?? "",
      justification: props.notice.justification ?? "",
      projectsAffected: initialProjectIds.length > 0,
      impacts: props.notice.impacts ?? "",
    });
    setShowEditDialog(true);
  }, [form, props.notice, initialProjectIds]);

  return (
    <>
      <Paper variant="infoCard" sx={{ mt: 2, width: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h5">
            {t(
              "ucet.organizationChangeNotice.detail.title",
              "Detail oznámení změny"
            )}
          </Typography>
          {props.onBack && (
            <Button variant="text" onClick={props.onBack}>
              {t("ucet.organizationChangeNotice.detail.back", "Zpět na seznam")}
            </Button>
          )}
        </Stack>

        <Grid container spacing={6}>
          <Grid item sm={props.hideTasks ? 12 : 6}>
            <Stack spacing={2}>
              {canEdit && (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenEdit}
                  >
                    {t("ucet.organizationChangeNotice.edit.button", "Editovat")}
                  </Button>
                </Stack>
              )}
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t("ucet.organizationChangeNotice.table.id", "ID oznámení")}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {props.notice.id}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      "ucet.organizationChangeNotice.subject",
                      "Předmět změny"
                    )}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{props.notice.subject}</strong>
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t("ucet.organizationChangeNotice.table.status", "Stav")}
                  </Typography>
                  <Chip
                    label={String(t(status.label, status.defaultValue))}
                    size="small"
                    sx={{ backgroundColor: status.backgroundColor }}
                  />
                </Stack>
                <Divider sx={{ my: 2 }} />
                {props.notice.description && (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>
                        {t(
                          "ucet.organizationChangeNotice.description",
                          "Popis požadované nebo ohlašované změny"
                        )}
                      </strong>
                    </Typography>
                    <DistaMarkdownViewer value={props.notice.description} />
                    <Divider sx={{ my: 2 }} />
                  </Stack>
                )}
                {props.notice.justification && (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>
                        {t(
                          "ucet.organizationChangeNotice.justification",
                          "Zdůvodnění změny"
                        )}
                      </strong>
                    </Typography>
                    <DistaMarkdownViewer value={props.notice.justification} />
                    <Divider sx={{ my: 2 }} />
                  </Stack>
                )}
                {(() => {
                  const filteredAffectedProjects =
                    loggedAccount.providerId == null
                      ? props.notice.affectedProjects
                      : props.notice.affectedProjects.filter(
                          (ap) =>
                            ap.providerUrn ===
                            new SistaId(
                              "providers",
                              loggedAccount.providerId!
                            ).asUrn()
                        );
                  const allProjectUrns = filteredAffectedProjects.flatMap(
                    (ap) => ap.projectUrns
                  );
                  const hasProjects = allProjectUrns.length > 0;
                  return (
                    <>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          <strong>
                            {t(
                              "ucet.organizationChangeNotice.projectsAffected",
                              "Změna má dopad na projekty"
                            )}
                          </strong>
                        </Typography>
                        {hasProjects ? (
                          <DoneIcon color="success" fontSize="small" />
                        ) : (
                          <CloseIcon color="error" fontSize="small" />
                        )}
                      </Stack>
                      <Divider sx={{ my: 2 }} />
                      {props.notice.impacts && (
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>
                              {t(
                                "ucet.organizationChangeNotice.impacts",
                                "Dopady změny do projektů"
                              )}
                            </strong>
                          </Typography>
                          <DistaMarkdownViewer value={props.notice.impacts} />
                          <Divider sx={{ my: 2 }} />
                        </Stack>
                      )}
                      {hasProjects && !props.hideAffectedProjects && (
                        <Stack>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>
                              {t(
                                "ucet.organizationChangeNotice.affectedProjects",
                                "Dotčené projekty"
                              )}
                            </strong>
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {allProjectUrns.map((urn) => (
                              <SistaChip
                                key={urn}
                                authToken={loggedAccount.authKey}
                                urn={urn}
                                variant="tiny"
                              />
                            ))}
                          </Stack>
                          <Divider sx={{ my: 2 }} />
                        </Stack>
                      )}
                    </>
                  );
                })()}
              </Stack>

              <Typography variant="h6">
                {t(
                  "ucet.organizationChangeNotice.identificationData",
                  "Identifikační údaje"
                )}
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      "ucet.organizationChangeNotice.orgName",
                      "Úřední název subjektu"
                    )}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{props.organization.displayName}</strong>
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t("ucet.organizationChangeNotice.ico", "IČO")}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{props.organization.idNumber ?? "-"}</strong>
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t("ucet.organizationChangeNotice.dic", "DIČ")}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{props.organization.vatId ?? "-"}</strong>
                  </Typography>
                </Stack>
              </Stack>

              <Typography variant="h6">
                {t("ucet.organizationChangeNotice.attachments", "Přílohy")}
              </Typography>
              <SistaFistoView
                authKey={loggedAccount.authKey}
                category="organizationChangeMeta"
                context={fistoContext}
                uploadDisabled={!canEdit}
              />

              {props.notice.confirmationFileId && (
                <>
                  <Typography variant="h6">
                    {t(
                      "ucet.organizationChangeNotice.confirmationFile",
                      "Potvrzení oznámení"
                    )}
                  </Typography>
                  <SistaFistoSingle
                    authKey={loggedAccount.authKey}
                    fileId={props.notice.confirmationFileId}
                    deleteDisabled={true}
                  />
                </>
              )}
            </Stack>
          </Grid>
          {!props.hideTasks && (
            <Grid item sm={6}>
              <SistaSpravaUkoluList
                loggedAccount={loggedAccount}
                loggedAuthKey={loggedAccount.authKey}
                tasksContext={taskContext}
                spravaUkoluRoot={"/sprava-ukolu"}
                onSuccess={() => {
                  props.onWorkflowAction?.();
                  props.onNoticeUpdated?.();
                }}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {showEditDialog && (
        <DistaDialog open onClose={() => setShowEditDialog(false)}>
          <DistaDialogHeader
            onClose={() => setShowEditDialog(false)}
            title={t(
              "ucet.organizationChangeNotice.edit.title",
              "Editovat oznámení změny"
            )}
          />
          <DialogContent>
            <FormContainer formContext={form} onSuccess={handleEditSubmit}>
              <Stack spacing={2} sx={{ my: 2 }}>
                <OrganizationChangeNoticeFormFields
                  form={form}
                  organizationId={props.organization.id}
                  authKey={loggedAccount.authKey}
                  selectedProjects={selectedProjects}
                  onProjectsChange={(projects) => {
                    setSelectedProjects(projects);
                    if (projects.length > 0) setProjectsError(false);
                  }}
                  projectsError={projectsError}
                  initialProjectIds={initialProjectIds}
                  hasInteractedRef={hasInteracted}
                />
                <AlertsBarContainer
                  id="alerts-edit-notice"
                  variant={AlertsBarVariant.relative}
                />
                <Stack direction="row" justifyContent="space-between">
                  <Button
                    variant="text"
                    onClick={() => setShowEditDialog(false)}
                  >
                    {t("ucet.organizationChangeNotice.close", "Zavřít")}
                  </Button>
                  <SmartButton
                    state={updateNoticeState}
                    variant="contained"
                    onClick={() => form.handleSubmit(handleEditSubmit)()}
                  >
                    {t(
                      "ucet.organizationChangeNotice.edit.save",
                      "Uložit změnu"
                    )}
                  </SmartButton>
                </Stack>
              </Stack>
            </FormContainer>
          </DialogContent>
        </DistaDialog>
      )}
    </>
  );
};
